import React, { memo, useEffect, useRef, useState } from 'react';
import {
	closestCenter,
	DndContext,
	KeyboardSensor,
	PointerSensor,
	useDroppable,
	useSensor,
	useSensors
} from '@dnd-kit/core';
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy
} from '@dnd-kit/sortable';

import KanbanColumn from '@features/project-management-tools/components/KanbanColumn';
import { useUpdateTaskMutation } from '@features/project-management-tools/api/projectManagementApi';
import { calculateNewSl } from '@features/project-management-tools/utils';
import SortableProjectCard from '@features/project-management-tools/components/SortableProjectCard';
import DragOverlayForKanban from '@features/project-management-tools/components/DragOverlayForKanban';

import { toastError } from '@/components/Toast';

const DroppableColumn = memo(({ title, tasks, className }) => {
	const isEmpty = tasks.length === 0;
	const droppableId = title;

	const { setNodeRef } = useDroppable({
		id: droppableId
	});

	return (
		<KanbanColumn
			title={title}
			count={tasks.length}
			className={className}
			statusColor={tasks[0]?.taskStatusTagResponse?.colorCode}
		>
			<SortableContext
				items={tasks.map((task) => task?.id?.toString())}
				strategy={verticalListSortingStrategy}
			>
				<div ref={setNodeRef}>
					{isEmpty ? (
						<div
							style={{
								minHeight: 80,
								display: 'hidden'
							}}
						></div>
					) : (
						tasks.map((task) => (
							<SortableProjectCard
								key={task?.id?.toString()}
								id={task?.id?.toString()}
								projectInfo={task}
								isDetailsPage={true}
							/>
						))
					)}
				</div>
			</SortableContext>
		</KanbanColumn>
	);
});

const ProjectDetailsKanbanView = ({
	groupedTasks = {},
	setGroupedTasks = () => {},
	projectId,
	refetchTasks,
	refetchProjectTags
}) => {
	const [updateTask] = useUpdateTaskMutation();
	const [activeDragTask, setActiveDragTask] = useState(null);
	const mainContainerRef = useRef(null);
	const scrollBarRef = useRef(null);
	const [visibleGroups, setVisibleGroups] = useState([]);

	// Set up DnD sensors with INCREASED DELAY
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				delay: 200,
				tolerance: 5
			}
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates
		})
	);

	const handleScrollBarScroll = (e) => {
		if (mainContainerRef.current) {
			mainContainerRef.current.scrollLeft = e.target.scrollLeft;
		}
	};

	const handleDragStart = (event) => {
		const { active } = event;
		const activeId = active.id.toString();

		let foundTask = null;
		for (const groupKey of Object.keys(groupedTasks)) {
			const tasks = groupedTasks[groupKey].tasks;
			const task = tasks.find((t) => t.id?.toString() === activeId);
			if (task) {
				foundTask = task;
				break;
			}
		}
		setActiveDragTask(foundTask);
	};

	const findContainer = (id) => {
		if (id in groupedTasks) return id;

		const taskId = id.toString();
		for (const [container, groupData] of Object.entries(groupedTasks)) {
			if (groupData.tasks.some((task) => task.id?.toString() === taskId)) {
				return container;
			}
		}

		return null;
	};

	// Handle drag end (Triggers when the task is dropped)
	const handleDragEnd = (event) => {
		const { active, over } = event;
		if (!active || !over) return;

		const activeId = active.id.toString();
		const overId = over.id.toString();

		const isOverGroup = Object.keys(groupedTasks).includes(overId);

		const activeContainer = findContainer(activeId);
		if (!activeContainer) {
			setActiveDragTask(null);
			return;
		}

		const activeIndex = groupedTasks[activeContainer].tasks.findIndex(
			(task) => task.id?.toString() === activeId
		);

		if (isOverGroup) {
			// Moving to an empty group
			const { updatedGroups, newTaskData } = moveToEmptyGroup(
				groupedTasks,
				activeContainer,
				activeIndex,
				overId
			);

			updateTask({
				body: newTaskData,
				projectId
			})
				.unwrap()
				.then(() => {
					refetchTasks();
					refetchProjectTags();
				})
				.catch((error) => {
					toastError(error?.data?.message || 'Failed to update task');
				});

			setGroupedTasks(updatedGroups);
		} else {
			const overContainer = findContainer(overId);
			if (!overContainer) {
				setActiveDragTask(null);
				return;
			}

			const overIndex = groupedTasks[overContainer].tasks.findIndex(
				(task) => task.id?.toString() === overId
			);

			if (activeContainer === overContainer) {
				// Reordering in the same container
				const { updatedGroups, newTaskData } = reorderWithinContainer(
					groupedTasks,
					activeContainer,
					activeIndex,
					overIndex
				);

				updateTask({
					body: newTaskData,
					projectId
				})
					.unwrap()
					.then(() => {
						refetchTasks();
						refetchProjectTags();
					})
					.catch((error) => {
						toastError(error?.data?.message || 'Failed to update task');
					});

				setGroupedTasks(updatedGroups);
			} else {
				// Moving to another container
				const { updatedGroups, newTaskData } = moveToContainer(
					groupedTasks,
					activeContainer,
					overContainer,
					activeIndex,
					overIndex
				);

				updateTask({
					body: newTaskData,
					projectId
				})
					.unwrap()
					.then(() => {
						refetchTasks();
						refetchProjectTags();
					})
					.catch((error) => {
						toastError(error?.data?.message || 'Failed to update task');
					});

				setGroupedTasks(updatedGroups);
			}
		}

		setActiveDragTask(null);
	};

	const moveToEmptyGroup = (groups, activeContainer, activeIndex, overId) => {
		const updatedGroups = { ...groups };
		const sourceTasks = [...updatedGroups[activeContainer].tasks];
		const [movedTask] = sourceTasks.splice(activeIndex, 1);

		if (!updatedGroups[overId]) {
			updatedGroups[overId] = {
				tasks: []
			};
		}

		const destTasks = [...updatedGroups[overId].tasks];
		const newSl = calculateNewSl(destTasks, destTasks.length);

		// Get the destination group's status information directly
		const destinationGroup = updatedGroups[overId];

		// Create status data with the correct id and text
		const statusData = {
			id: destinationGroup.id || null,
			text: overId
		};

		updatedGroups[activeContainer].tasks = sourceTasks;
		updatedGroups[overId].tasks = [...destTasks, movedTask];

		return {
			updatedGroups,
			newTaskData: {
				id: movedTask.id,
				sl: Number(newSl).toFixed(2),
				taskStatusTagRequest: statusData
			}
		};
	};

	const reorderWithinContainer = (groups, container, activeIndex, overIndex) => {
		const updatedGroups = { ...groups };
		const tasks = [...updatedGroups[container].tasks];
		const newTasks = arrayMove(tasks, activeIndex, overIndex);
		const newSl = calculateNewSl(tasks, overIndex);

		updatedGroups[container].tasks = newTasks;

		const task = tasks[activeIndex];
		const statusData = task.taskStatusTagResponse;

		return {
			updatedGroups,
			newTaskData: {
				id: task.id,
				sl: Number(newSl).toFixed(2),
				taskStatusTagRequest: statusData ? { id: statusData.id, text: statusData.text } : null
			}
		};
	};

	const moveToContainer = (groups, activeContainer, overContainer, activeIndex, overIndex) => {
		const updatedGroups = { ...groups };
		const sourceTasks = [...updatedGroups[activeContainer].tasks];
		const destTasks = [...updatedGroups[overContainer].tasks];
		const [movedTask] = sourceTasks.splice(activeIndex, 1);

		const targetIndex = overIndex !== -1 ? overIndex : destTasks.length;
		const newSl = calculateNewSl(destTasks, targetIndex);

		// Get the destination group's status information
		const destinationGroup = updatedGroups[overContainer];

		// Create a new status tag with the correct id and text
		const statusData = {
			id: destinationGroup.id || null,
			text: overContainer
		};

		if (overIndex !== -1) {
			destTasks.splice(overIndex, 0, movedTask);
		} else {
			destTasks.push(movedTask);
		}

		updatedGroups[activeContainer].tasks = sourceTasks;
		updatedGroups[overContainer].tasks = destTasks;

		return {
			updatedGroups,
			newTaskData: {
				id: movedTask.id,
				sl: Number(newSl).toFixed(2),
				taskStatusTagRequest: statusData
			}
		};
	};

	useEffect(() => {
		const handleScroll = () => {
			if (!mainContainerRef.current) return;
			const container = mainContainerRef.current;
			const children = Array.from(container.children);
			const scrollLeft = container.scrollLeft;
			const containerWidth = container.offsetWidth;

			const visible = [];
			children.forEach((child, idx) => {
				const childLeft = child.offsetLeft;
				const childRight = childLeft + child.offsetWidth;
				if (childLeft < scrollLeft + containerWidth && childRight > scrollLeft) {
					visible.push(idx);
				}
			});
			setVisibleGroups(visible);
		};

		const container = mainContainerRef.current;
		if (container) {
			container.addEventListener('scroll', handleScroll);
			handleScroll(); // Initial
		}
		return () => {
			if (container) container.removeEventListener('scroll', handleScroll);
		};
	}, [groupedTasks]);

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragStart={handleDragStart}
			onDragEnd={handleDragEnd}
		>
			<div className="task-list-container">
				<div
					ref={mainContainerRef}
					className="task-all-row kanban-all-tasks d-flex"
					style={{ overflowX: 'auto', paddingBottom: '2rem' }}
				>
					{groupedTasks &&
						Object.keys(groupedTasks).map((group) => (
							<DroppableColumn
								key={group}
								title={group}
								tasks={groupedTasks[group]?.tasks || []}
								className={group.className}
							/>
						))}
				</div>
				<div
					className="project-kanban-scroll-x-label"
					ref={scrollBarRef}
					onScroll={handleScrollBarScroll}
				>
					<div
						style={{
							width: mainContainerRef.current?.scrollWidth || '100%',
							height: '100%'
						}}
					/>
				</div>

				{/* Mini-map */}
				<div className="scroller-mini-map shadow-8dp">
					{Object.keys(groupedTasks).map((group, idx) => (
						<div
							className="mini-map-item"
							key={group}
							style={{
								background: visibleGroups.includes(idx) ? '#eaf3ff' : '#f5f5f5',
								border: visibleGroups.includes(idx)
									? '1px solid rgb(11, 88, 165)'
									: '1px solid #e0e0e0'
							}}
						/>
					))}
				</div>
			</div>

			<DragOverlayForKanban activeDragTask={activeDragTask} />
		</DndContext>
	);
};

export default ProjectDetailsKanbanView;
